import React, { useState } from 'react';
import DatePicker from 'react-mobile-datepicker';
import { DateTime } from 'luxon';
import { useI18n } from '~Hooks';
import styled from 'styled-components';

// sm scales the entire picker down to 70% via transform
const SM_SCALE = 0.7;

const Wrapper = styled.div<{ dark?: boolean; fullWidth?: boolean; size?: 'sm' | 'md' }>`
  width: 100%;
  min-width: 200px;
  height: ${({ size }) => (size === 'sm' ? `${200 * SM_SCALE}px` : '200px')};
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  .datepicker {
    width: ${({ size }) => (size === 'sm' ? `${100 / SM_SCALE}%` : '100%')};
    top: 0;
    bottom: 0;
    transform-origin: top left;
    transform: ${({ size }) => (size === 'sm' ? `scale(${SM_SCALE})` : 'none')};
  }

  .datepicker.ios {
    background-color: ${({ dark }) => (dark ? 'var(--colorBlueGray1100)' : 'var(--colorWhite)')};
  }

  .datepicker .datepicker-content {
    padding: 0 !important;
    justify-content: center;
  }

  .datepicker-col-1 {
    flex: 1;
  }

  .datepicker.ios .datepicker-viewport::after {
    background: ${({ dark }) => {
      const base = dark ? 'var(--colorBlueGray1100)' : 'var(--colorWhite)';
      const transparent = dark ? 'rgba(10, 10, 10, 0)' : 'rgba(245, 245, 245, 0)';

      return `linear-gradient(
    ${base},
    ${transparent} 52%,
    ${transparent} 48%,
    ${base}
  )`;
    }};
  }

  ${({ dark }) =>
    dark &&
    `
    .datepicker.ios .datepicker-scroll li {
      color: var(--colorWhite);
    }

    .datepicker.ios .datepicker-scroll li.disabled {
      color: var(--colorBlueGray400);
    }

    .datepicker.ios .datepicker-wheel {
      border-top: 1px solid var(--colorBlueGray800);
      border-bottom: 1px solid var(--colorBlueGray800);
    }
  `}
`;

interface Props {
  time?: string | null;
  dark?: boolean;
  fullWidth?: boolean;
  quarterHourSteps?: boolean;
  timeOnly?: boolean;
  initialValue?: Date;
  size?: 'sm' | 'md';
  /** Earliest selectable date. Defaults to "now" (no past selection). */
  min?: Date;
  /** Latest selectable date. Defaults to now + 30 days. */
  max?: Date;
  onChange: (val: string | null) => void;
}

/**
 * DateTmePicker component
 */

const DateTimePicker: React.FC<Props> = ({
  onChange,
  dark,
  fullWidth,
  quarterHourSteps,
  timeOnly,
  initialValue,
  size = 'md',
  time,
  min,
  max,
}) => {
  const { i18n } = useI18n();
  const currentDate = new Date();
  if (quarterHourSteps) {
    currentDate.setMinutes(Math.floor(currentDate.getMinutes() / 15) * 15, 0, 0);
  }
  const startDate = initialValue || new Date(currentDate);
  const [chosenDate, setChosenDate] = useState(startDate);
  /** Default lower bound is "now" (existing behavior); upper bound is now + 30d. */
  const minDate = min ?? currentDate;
  const defaultMax = new Date();
  defaultMax.setDate(defaultMax.getDate() + 30);
  const maxDate = max ?? defaultMax;

  const months: { [key: string]: string } = {
    '1': i18n('Date.Jan'),
    '2': i18n('Date.Feb'),
    '3': i18n('Date.Mars'),
    '4': i18n('Date.Apr'),
    '5': i18n('Date.May'),
    '6': i18n('Date.June'),
    '7': i18n('Date.July'),
    '8': i18n('Date.Aug'),
    '9': i18n('Date.Sep'),
    '10': i18n('Date.Oct'),
    '11': i18n('Date.Nov'),
    '12': i18n('Date.Dec'),
  };

  const days = [
    i18n('Date.Sun'),
    i18n('Date.Mon'),
    i18n('Date.Tue'),
    i18n('Date.Wed'),
    i18n('Date.Thu'),
    i18n('Date.Fri'),
    i18n('Date.Sat'),
  ];

  const datePickerConfig = {
    ...(timeOnly
      ? {}
      : {
          date: {
            format: (value: Date) =>
              days[value.getDay()] +
              ' ' +
              value.getDate() +
              ' ' +
              months[value.getMonth() + 1] +
              '.',
            caption: 'Day',
            step: 1,
          },
        }),
    hour: {
      format: 'hh',
      caption: 'Hour',
      step: 1,
    },
    minute: {
      format: 'mm',
      caption: 'Min',
      step: quarterHourSteps ? 15 : 1,
    },
  };

  const handleChange = (val: Date) => {
    setChosenDate(val);

    onChange(DateTime.fromJSDate(val).toISO());
  };

  return (
    <Wrapper dark={dark} fullWidth={fullWidth} size={size}>
      <DatePicker
        isPopup={false}
        showHeader={false}
        showFooter={false}
        theme="ios"
        dateConfig={datePickerConfig}
        min={minDate}
        max={maxDate}
        value={chosenDate}
        onChange={(val: Date) => handleChange(val)}
      />
    </Wrapper>
  );
};

export default DateTimePicker;
